import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://agwnmyebk4.execute-api.us-east-1.amazonaws.com/dev'

export interface User {
  id: string
  sub: string
  email: string
  name: string
  role: 'student' | 'parent' | 'admin'
  plan: 'free' | 'pro'
  effectivePlan: 'free' | 'pro'
  parentId: string | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name: string, role?: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  syncUserProfile: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })
        if (!response.ok) throw new Error('Login failed')
        const data = await response.json()

        set({
          user: {
            ...data.user,
            plan: data.user.plan || 'free',
            effectivePlan: data.user.plan || 'free',
            parentId: data.user.parentId || null,
          },
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
        })

        // Sync full profile (plan, role, effectivePlan, parentId) from DynamoDB
        // Awaited so that role is correct for post-login routing
        await get().syncUserProfile()
      },

      register: async (email: string, password: string, name: string, role = 'student') => {
        const response = await fetch(`${API_BASE_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name, role }),
        })
        if (!response.ok) throw new Error('Registration failed')
        const data = await response.json()

        set({
          user: {
            ...data.user,
            plan: 'free',
            effectivePlan: 'free',
            parentId: null,
          },
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
        })
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) throw new Error('No refresh token')

        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
        if (!response.ok) {
          get().logout()
          throw new Error('Token refresh failed')
        }
        const data = await response.json()
        set({ accessToken: data.accessToken })
      },

      /**
       * Sync role, plan, effectivePlan and parentId from DynamoDB via /auth/me.
       * Called after login and after billing changes.
       */
      syncUserProfile: async () => {
        const { accessToken, user } = get()
        if (!accessToken || !user) return

        try {
          const response = await fetch(`${API_BASE_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          })
          if (!response.ok) return
          const profile = await response.json()

          set({
            user: {
              ...user,
              role: profile.role || user.role,
              plan: profile.plan || 'free',
              effectivePlan: profile.effective_plan || profile.plan || 'free',
              parentId: profile.parent_id || null,
            },
          })
        } catch {
          // Non-critical — ignore errors in background sync
        }
      },
    }),
    { name: 'kapri-auth' }
  )
)
